import React from 'react';
import {
  Box,
  Paper,
  Typography
} from '@mui/material';
import {
  Sankey,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { formatNumber } from '../../../../lib/utils/conversionFunnelUtils';

/**
 * SankeyDiagramComponent
 * 
 * Visualizes audience flow through the conversion funnel by demographic segment
 * Shows how different demographic cohorts progress through each stage
 */
const SankeyDiagramComponent = ({ nodes, links, title, height = 600 }) => {
  if (!nodes || nodes.length === 0 || !links || links.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No flow data available for visualization
        </Typography>
      </Paper>
    );
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      
      // Check if it's a node or link
      if (data.name) {
        // Node tooltip
        return (
          <Paper sx={{ p: 2, maxWidth: 250 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {data.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Segment: {data.segment || data.category}
            </Typography>
            {data.value && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Audience:</strong> {formatNumber(data.value)}
              </Typography>
            )}
          </Paper>
        );
      } else if (data.source !== undefined && data.target !== undefined) {
        // Link tooltip
        const sourceNode = nodes[data.source];
        const targetNode = nodes[data.target];
        
        return (
          <Paper sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Flow: {sourceNode?.segment || 'Unknown'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              From: {sourceNode?.stageName || 'Unknown'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              To: {targetNode?.stageName || 'Unknown'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Audience:</strong> {formatNumber(data.value)}
            </Typography>
          </Paper>
        );
      }
    }
    return null;
  };

  // Custom node rendering
  const CustomNode = ({ x, y, width, height, index, payload, containerWidth }) => {
    const node = payload;
    const isOut = x + width + 6 > containerWidth;
    
    return (
      <g>
        {/* Node rectangle */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={node.color || '#999'}
          opacity={0.8}
          rx={4}
        />
        
        {/* Node label */}
        <text
          x={isOut ? x - 6 : x + width + 6}
          y={y + height / 2}
          textAnchor={isOut ? 'end' : 'start'}
          fontSize={12}
          fontWeight={600}
          fill="#333"
        >
          {node.segment || node.name}
        </text>
      </g>
    );
  };

  // Custom link rendering
  const CustomLink = ({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, index, payload }) => {
    const link = payload;
    const sourceNode = nodes[link.source];
    
    return (
      <path
        d={`
          M${sourceX},${sourceY + linkWidth / 2}
          C${sourceControlX},${sourceY + linkWidth / 2}
            ${targetControlX},${targetY + linkWidth / 2}
            ${targetX},${targetY + linkWidth / 2}
        `}
        stroke={sourceNode?.color || '#999'}
        strokeWidth={linkWidth}
        fill="none"
        opacity={0.3}
        style={{ transition: 'opacity 0.3s' }}
        onMouseEnter={(e) => {
          e.target.style.opacity = 0.6;
        }}
        onMouseLeave={(e) => {
          e.target.style.opacity = 0.3;
        }}
      />
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      {title && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Flow shows how demographic segments progress through each stage
          </Typography>
        </Box>
      )}

      <Box sx={{ width: '100%', height }}>
        <ResponsiveContainer>
          <Sankey
            data={{ nodes, links }}
            node={<CustomNode />}
            link={<CustomLink />}
            nodePadding={20}
            margin={{ top: 20, right: 150, bottom: 20, left: 150 }}
          >
            <Tooltip content={<CustomTooltip />} />
          </Sankey>
        </ResponsiveContainer>
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          How to Read This Diagram
        </Typography>
        <Typography variant="caption" color="text.secondary">
          • Each colored band represents a demographic segment
          <br />
          • Width of the flow indicates audience size
          <br />
          • Narrowing flows show drop-offs between stages
          <br />
          • Hover over flows for detailed numbers
        </Typography>
      </Box>
    </Paper>
  );
};

export default SankeyDiagramComponent;

